import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import classNames from "classnames";
import {
  action,
  entries,
  IComputedValue,
  reaction,
  runInAction,
  values,
} from "mobx";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { atom } from "../atom";
import { PaneViewMode, Position, Rect } from "../Types";
import { FeatherIcon } from "../ui/FeatherIcon";
import { KeyboardShortcutIndicator } from "../ui/KeyboardShortcutIndicator";
import {
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from "../ui/Menu";
import { Tooltip } from "../ui/Tooltip";
import {
  activePaneStates,
  canvasViewport,
  canvasViewportShouldAnimateAtom,
  focusedPaneIdAtom,
  headerHeightComputed,
  selectedPaneIdsAtom,
} from "./CanvasState";
import { toggleMinimap } from "./Minimap";
import {
  getZoomWithDirection,
  resetCanvasOffset,
  setSelectedToolToPointer,
  zoomToLevel,
} from "./SandboxUtils";

type ZoomToolState = "in" | "out";

let zoomFunction: ((viewportTarget: Position) => void) | undefined;
const zoomShield = document.createElement("div");
zoomShield.className = "fixed top-0 left-0 right-0 bottom-0 z-99999";
zoomShield.addEventListener("click", (e) => {
  if (zoomFunction !== undefined) {
    zoomFunction([e.clientX, e.clientY]);
  }
});
zoomShield.addEventListener("mousedown", (e) => {
  // prevent zoom click from selecting text
  e.preventDefault();
});

export function setupZoom(state: ZoomToolState | undefined) {
  if (state === undefined) {
    zoomFunction = undefined;
    if (zoomShield.parentNode !== null) {
      zoomShield.parentNode.removeChild(zoomShield);
    }
  } else {
    zoomFunction = (viewportTarget: Position) => {
      const nextZoom = getZoomWithDirection(canvasViewport.zoom, state);
      if (nextZoom !== undefined) {
        zoomToLevel(nextZoom, viewportTarget);
      }
    };
    zoomShield.style.cursor = state === "in" ? "zoom-in" : "zoom-out";
    document.body.appendChild(zoomShield);
  }
}

export function calculateZoomToRectAtoms(
  rectAtoms: IComputedValue<Rect>[],
  alignment: ViewportSelectionAlignment = ViewportSelectionAlignment.Center
): [canvasOffset: Position, zoom: number] | undefined {
  if (rectAtoms.length === 0) {
    return undefined;
  }
  const firstRect = rectAtoms[0].get();
  let [minX, minY] = firstRect;
  let maxX = minX + firstRect[2];
  let maxY = minY + firstRect[3];
  for (let i = 1; i < rectAtoms.length; i++) {
    const rect = rectAtoms[i].get();
    minX = Math.min(minX, rect[0]);
    minY = Math.min(minY, rect[1]);
    maxX = Math.max(maxX, rect[0] + rect[2]);
    maxY = Math.max(maxY, rect[1] + rect[3]);
  }
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight - headerHeightComputed.get();
  const contentWidth = maxX - minX;
  const contentHeight = maxY - minY;
  const zoomTarget =
    Math.round(
      Math.min(
        viewportWidth / (contentWidth + 64),
        viewportHeight / (contentHeight + 64),
        1
      ) * 100
    ) / 100;
  if (alignment === ViewportSelectionAlignment.TopLeft) {
    return [[minX - 32, minY - 32], zoomTarget];
  }
  if (alignment === ViewportSelectionAlignment.BottomRight) {
    return [
      [
        maxX + 32 - viewportWidth / zoomTarget,
        maxY + 32 - viewportHeight / zoomTarget,
      ],
      zoomTarget,
    ];
  }
  const contentCenter: Position = [
    minX + contentWidth / 2,
    minY + contentHeight / 2,
  ];
  return [
    [
      contentCenter[0] - viewportWidth / 2 / zoomTarget,
      contentCenter[1] - viewportHeight / 2 / zoomTarget,
    ],
    zoomTarget,
  ];
}

export function zoomToFit(
  paneIds?: string[],
  alignment: ViewportSelectionAlignment = ViewportSelectionAlignment.Center
) {
  runInAction(() => {
    const filteredActivePaneStates =
      paneIds !== undefined
        ? entries(activePaneStates)
            .filter(([paneId]) => paneIds.includes(paneId))
            .map(([, activePaneState]) => activePaneState)
        : values(activePaneStates);
    const rectsComputed = filteredActivePaneStates
      .filter(
        (activePaneState) =>
          activePaneState.viewMode.get() !== PaneViewMode.Hidden
      )
      .map((activePaneState) => activePaneState.rect);
    const rv = calculateZoomToRectAtoms(rectsComputed, alignment);
    if (rv !== undefined) {
      const [canvasOffset, zoom] = rv;
      canvasViewportShouldAnimateAtom.value = true;
      canvasViewport.offset = canvasOffset;
      canvasViewport.zoom = zoom;
    }
  });
}

export enum ViewportSelectionAlignment {
  Center,
  TopLeft,
  BottomRight,
}
export function zoomToSelection(
  alignment: ViewportSelectionAlignment = ViewportSelectionAlignment.Center
) {
  zoomToFit(
    selectedPaneIdsAtom.value.length > 0
      ? selectedPaneIdsAtom.value
      : focusedPaneIdAtom.value !== undefined
      ? [focusedPaneIdAtom.value]
      : undefined,
    alignment
  );
}

function ZoomMenu() {
  return (
    <>
      <DropdownMenuItem
        onClick={() => {
          const nextZoom = getZoomWithDirection(canvasViewport.zoom, "in");
          if (nextZoom !== undefined) {
            zoomToLevel(nextZoom);
          }
        }}
      >
        zoom in
        <KeyboardShortcutIndicator>+</KeyboardShortcutIndicator>
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => {
          const nextZoom = getZoomWithDirection(canvasViewport.zoom, "out");
          if (nextZoom !== undefined) {
            zoomToLevel(nextZoom);
          }
        }}
      >
        zoom out
        <KeyboardShortcutIndicator>-</KeyboardShortcutIndicator>
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => {
          zoomToFit();
        }}
      >
        zoom to fit
        <KeyboardShortcutIndicator>⇧1</KeyboardShortcutIndicator>
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => {
          zoomToSelection();
        }}
        className="gap-2"
      >
        zoom to selection
        <KeyboardShortcutIndicator>⇧2</KeyboardShortcutIndicator>
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => {
          zoomToLevel(1);
        }}
      >
        reset zoom
        <KeyboardShortcutIndicator>⇧0</KeyboardShortcutIndicator>
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={action(() => {
          canvasViewportShouldAnimateAtom.value = true;
          resetCanvasOffset();
          canvasViewport.zoom = 1;
        })}
      >
        reset view
        <KeyboardShortcutIndicator>0</KeyboardShortcutIndicator>
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={action(() => {
          toggleMinimap();
        })}
      >
        minimap
        <KeyboardShortcutIndicator>m</KeyboardShortcutIndicator>
      </DropdownMenuItem>
    </>
  );
}

export const ToolbarZoomButton = observer(() => {
  const [localZoomAtom] = useState(() => atom(canvasViewport.zoom));
  useEffect(() => {
    return reaction(
      () => canvasViewport.zoom,
      (zoom) => {
        localZoomAtom.value = Math.round(zoom * 100) / 100;
      },
      {
        delay: 50,
      }
    );
  }, []);
  const [open, setOpen] = useState(false);
  return (
    <DropdownMenuRoot onOpenChange={setOpen} modal={false}>
      <Tooltip
        content={
          <div>
            <div>
              <span className="font-semibold">Z</span> to zoom in
            </div>
            <div>
              <span className="font-semibold">⌥Z</span> to zoom out
            </div>
          </div>
        }
        disabled={open}
      >
        <DropdownMenuTrigger asChild={true}>
          <button
            onMouseDown={() => {
              setSelectedToolToPointer();
            }}
            className="h-full group relative"
          >
            <div
              className={classNames(
                "h-full flex items-center justify-center w-16 pr-5",
                open ? "bg-secondary-100" : "group-hover:bg-neutral-50"
              )}
            >
              {Math.round(localZoomAtom.value * 100)}%
            </div>
            <div
              className={classNames(
                "flex items-center absolute pl-0.5 pr-3 right-0 top-0 bottom-0 group2",
                {
                  "focus:outline-none": open,
                }
              )}
            >
              <FeatherIcon
                icon="chevron-down"
                size="sm"
                className={classNames(
                  "transform transition-transform",
                  open ? "translate-y-0.5" : "group2-hover:translate-y-0.5"
                )}
              />
            </div>
          </button>
        </DropdownMenuTrigger>
      </Tooltip>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          align="end"
          className="relative border-t-2 flex flex-col border-secondary-100 bg-neutral-0 py-1.5 rounded-b shadow-xl min-w-[7.5rem] popover-scale-in"
        >
          <ZoomMenu />
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuRoot>
  );
});
